import React, { useEffect, useState, useRef, useCallback } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Upload, Loader2, Search, Copy, Check, Trash2, FolderOpen, HardDrive,
  Image as ImageIcon, X, AlertCircle, CheckCircle2, Pause, Play, ExternalLink, Info,
} from "lucide-react";
import { toast } from "sonner";

const CONCURRENCY = 4;      // Parallel uploads (tune to match backend bandwidth)
const MAX_PER_BATCH = 500;  // Safety cap per batch

export default function MediaAdmin() {
  const [data, setData] = useState({ items: [], count: 0, total_size: 0, folders: [] });
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeFolder, setActiveFolder] = useState("");
  const [uploadFolder, setUploadFolder] = useState("misc");
  const [copied, setCopied] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [usageFilter, setUsageFilter] = useState("all"); // all | used | unused

  // Batch upload state
  const [queue, setQueue] = useState([]);        // [{id, file, status:'pending|uploading|done|error', progress:0, error?}]
  const [uploadingActive, setUploadingActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const cancelRef = useRef(false);
  const inputRef = useRef(null);

  const load = async (f = activeFolder) => {
    setLoading(true);
    try {
      const [{ data: mediaData }, { data: usageData }] = await Promise.all([
        api.get("/media", { params: f ? { folder: f } : {} }),
        api.get("/media/usage").catch(() => ({ data: {} })),
      ]);
      setData(mediaData);
      setUsage(usageData || {});
    } catch { toast.error("Impossible de charger la médiathèque"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFolder]);

  const filtered = (data.items || []).filter((it) => {
    if (q && !(it.name.toLowerCase().includes(q.toLowerCase()) || it.folder.toLowerCase().includes(q.toLowerCase()))) return false;
    const refs = usage[it.url] || [];
    if (usageFilter === "used" && refs.length === 0) return false;
    if (usageFilter === "unused" && refs.length > 0) return false;
    return true;
  });

  const usageStats = {
    used: (data.items || []).filter((it) => (usage[it.url] || []).length > 0).length,
    unused: (data.items || []).filter((it) => (usage[it.url] || []).length === 0).length,
  };

  // ---------- BATCH UPLOAD ----------
  const addFilesToQueue = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/"));
    if (!files.length) { toast.error("Aucune image valide détectée"); return; }
    if (files.length > MAX_PER_BATCH) {
      toast.warning(`Limite par lot : ${MAX_PER_BATCH} fichiers. Seules les ${MAX_PER_BATCH} premières seront ajoutées.`);
      files.splice(MAX_PER_BATCH);
    }
    const items = files.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f, status: "pending", progress: 0, error: null,
    }));
    setQueue((q) => [...q, ...items]);
    if (!uploadingActive) startUpload(items);
  };

  const uploadOne = async (item) => {
    setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "uploading", progress: 0 } : x));
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("folder", uploadFolder || "misc");
    try {
      await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setQueue((q) => q.map((x) => x.id === item.id ? { ...x, progress: pct } : x));
          }
        },
      });
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "done", progress: 100 } : x));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Erreur upload";
      setQueue((q) => q.map((x) => x.id === item.id ? { ...x, status: "error", error: msg } : x));
    }
  };

  const startUpload = async (initialItems) => {
    if (uploadingActive) return;
    setUploadingActive(true);
    cancelRef.current = false;
    pausedRef.current = false;
    setPaused(false);

    // Use a continuous worker pool that picks next pending item from the queue state
    // We read from a local mutable snapshot merged with state updates
    const getNext = () => {
      let picked = null;
      setQueue((q) => {
        const idx = q.findIndex((x) => x.status === "pending");
        if (idx >= 0) {
          picked = q[idx];
          const next = [...q];
          next[idx] = { ...next[idx], status: "uploading", progress: 0 };
          return next;
        }
        return q;
      });
      // Wait one tick so the state update propagates before returning
      return new Promise((resolve) => setTimeout(() => resolve(picked), 10));
    };

    const worker = async () => {
      while (true) {
        if (cancelRef.current) return;
        while (pausedRef.current) {
          await new Promise((r) => setTimeout(r, 250));
          if (cancelRef.current) return;
        }
        const item = await getNext();
        if (!item) return;
        // Put back to pending (our uploadOne sets it to uploading again); avoid double mark by not re-marking
        await uploadOne(item);
      }
    };

    // Kick off N workers
    await Promise.all(Array.from({ length: CONCURRENCY }).map(() => worker()));

    setUploadingActive(false);
    // Refresh library after batch
    await load();
    // Keep queue visible for 4 seconds then clean completed if no errors
    setTimeout(() => setQueue((q) => q.filter((x) => x.status === "error")), 4000);
  };

  const cancelAll = () => {
    cancelRef.current = true;
    setQueue((q) => q.map((x) => x.status === "pending" ? { ...x, status: "error", error: "Annulé" } : x));
    toast.info("Upload annulé");
  };

  const togglePause = () => {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
  };

  const retryErrors = () => {
    setQueue((q) => q.map((x) => x.status === "error" ? { ...x, status: "pending", progress: 0, error: null } : x));
    if (!uploadingActive) startUpload();
  };

  const clearQueue = () => setQueue([]);

  const queueStats = {
    total: queue.length,
    done: queue.filter((x) => x.status === "done").length,
    error: queue.filter((x) => x.status === "error").length,
    uploading: queue.filter((x) => x.status === "uploading").length,
    pending: queue.filter((x) => x.status === "pending").length,
  };
  const overallPct = queueStats.total ? Math.round((queueStats.done / queueStats.total) * 100) : 0;

  // ---------- DRAG & DROP ----------
  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    addFilesToQueue(e.dataTransfer.files);
    // eslint-disable-next-line
  }, [uploadFolder, uploadingActive]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragOver(false); };

  const handleFileInput = (e) => {
    addFilesToQueue(e.target.files);
    if (inputRef.current) inputRef.current.value = "";
  };

  // ---------- ACTIONS ----------
  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(url); setTimeout(() => setCopied(null), 1500);
    toast.success("URL copiée");
  };

  const del = async (url) => {
    if (!window.confirm("Supprimer ce fichier ? Action irréversible.")) return;
    try {
      await api.delete("/media", { params: { url } });
      toast.success("Supprimé"); load();
    } catch { toast.error("Suppression impossible"); }
  };

  const totalKb = Math.round((data.total_size || 0) / 1024);
  const totalMb = (totalKb / 1024).toFixed(1);

  return (
    <div className="p-6 md:p-10" data-testid="admin-media-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Bibliothèque</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Médiathèque</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Glissez-déposez jusqu'à {MAX_PER_BATCH} photos d'un coup. Compression WebP automatique, upload parallèle ({CONCURRENCY} en simultané).</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)} className="h-10 px-3 rounded-md border border-[hsl(var(--border))] text-sm">
            <option value="misc">Dossier : misc</option>
            <option value="realisations">realisations</option>
            <option value="blog">blog</option>
            <option value="services">services</option>
            <option value="hero">hero</option>
            <option value="logos">logos</option>
            <option value="galerie">galerie</option>
          </select>
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileInput} data-testid="media-upload-input" />
          <Button onClick={() => inputRef.current?.click()} className="bg-[color:var(--brand-navy)] font-bold" data-testid="media-upload-btn">
            <Upload className="w-4 h-4 mr-1" />Choisir des fichiers
          </Button>
        </div>
      </header>

      {/* Dropzone */}
      <div
        onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
        className={`mb-6 rounded-2xl border-2 border-dashed transition-all p-6 md:p-8 text-center cursor-pointer ${
          dragOver ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5 scale-[1.01]" : "border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:border-[color:var(--brand-navy)]/40"
        }`}
        onClick={() => inputRef.current?.click()}
        data-testid="media-dropzone"
      >
        <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-[color:var(--brand-sky)]" : "text-[hsl(var(--muted-foreground))]"}`} />
        <div className="font-display font-bold text-[color:var(--brand-navy)]">
          {dragOver ? "Déposez ici pour uploader" : "Glissez-déposez vos photos ici"}
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
          Jusqu'à {MAX_PER_BATCH} fichiers · JPG, PNG, WEBP, HEIC · 8 Mo max par fichier · Dossier cible : <strong>{uploadFolder}</strong>
        </div>
      </div>

      {/* Active batch panel */}
      {queue.length > 0 && (
        <Card className="p-4 md:p-5 border border-[color:var(--brand-navy)]/30 mb-6 bg-white" data-testid="media-batch-panel">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="font-display font-bold text-[color:var(--brand-navy)] flex items-center gap-2">
                {uploadingActive ? <Loader2 className="w-4 h-4 animate-spin text-[color:var(--brand-sky)]" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                Lot d'upload · {queueStats.done}/{queueStats.total} terminés
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                {queueStats.uploading} en cours · {queueStats.pending} en attente · {queueStats.error} erreur(s)
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {uploadingActive && (
                <Button variant="outline" size="sm" onClick={togglePause} data-testid="media-batch-pause">
                  {paused ? <><Play className="w-3.5 h-3.5 mr-1" />Reprendre</> : <><Pause className="w-3.5 h-3.5 mr-1" />Pause</>}
                </Button>
              )}
              {queueStats.error > 0 && !uploadingActive && (
                <Button variant="outline" size="sm" onClick={retryErrors} data-testid="media-batch-retry">Réessayer les erreurs</Button>
              )}
              {uploadingActive ? (
                <Button variant="outline" size="sm" onClick={cancelAll} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="media-batch-cancel">
                  <X className="w-3.5 h-3.5 mr-1" />Annuler
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={clearQueue}>
                  <X className="w-3.5 h-3.5 mr-1" />Fermer
                </Button>
              )}
            </div>
          </div>
          {/* Overall bar */}
          <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden mb-3">
            <div className="h-full bg-gradient-to-r from-[color:var(--brand-sky)] to-[color:var(--brand-navy)] transition-all duration-300" style={{ width: `${overallPct}%` }} />
          </div>
          {/* File list (max 200 visible) */}
          <div className="max-h-60 overflow-y-auto border border-[hsl(var(--border))] rounded-lg divide-y">
            {queue.slice(0, 200).map((it) => (
              <div key={it.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                {it.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                {it.status === "error" && <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                {it.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin text-[color:var(--brand-sky)] shrink-0" />}
                {it.status === "pending" && <div className="w-4 h-4 rounded-full border border-[hsl(var(--muted-foreground))]/40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-[color:var(--brand-navy)]">{it.file.name}</div>
                  {it.status === "uploading" && (
                    <div className="h-1 rounded-full bg-[hsl(var(--muted))] mt-1 overflow-hidden">
                      <div className="h-full bg-[color:var(--brand-sky)]" style={{ width: `${it.progress}%` }} />
                    </div>
                  )}
                  {it.status === "error" && <div className="text-[10px] text-red-600 mt-0.5">{it.error}</div>}
                </div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono shrink-0">
                  {(it.file.size / 1024).toFixed(0)} Ko
                </div>
              </div>
            ))}
            {queue.length > 200 && <div className="text-center py-2 text-[11px] text-[hsl(var(--muted-foreground))]">+ {queue.length - 200} autres fichiers</div>}
          </div>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-4 border">
          <ImageIcon className="w-4 h-4 text-[color:var(--brand-sky)] mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Fichiers</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{data.count}</div>
        </Card>
        <Card className="p-4 border">
          <HardDrive className="w-4 h-4 text-emerald-500 mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Poids total</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{totalMb}<span className="text-base ml-1">Mo</span></div>
        </Card>
        <Card className="p-4 border">
          <FolderOpen className="w-4 h-4 text-amber-500 mb-2" />
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">Dossiers</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] tabular-nums">{(data.folders || []).length}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 border mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom…" className="pl-9 h-9" data-testid="media-search" />
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setUsageFilter("all")} className={`px-3 h-8 rounded-full text-xs font-semibold border ${usageFilter === "all" ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`} data-testid="media-filter-all">
            Tous
          </button>
          <button onClick={() => setUsageFilter("used")} className={`px-3 h-8 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${usageFilter === "used" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-[hsl(var(--border))] hover:bg-emerald-50"}`} data-testid="media-filter-used">
            <CheckCircle2 className="w-3 h-3" /> Utilisés ({usageStats.used})
          </button>
          <button onClick={() => setUsageFilter("unused")} className={`px-3 h-8 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 ${usageFilter === "unused" ? "bg-amber-500 text-white border-amber-500" : "bg-white border-[hsl(var(--border))] hover:bg-amber-50"}`} data-testid="media-filter-unused">
            <AlertCircle className="w-3 h-3" /> Orphelins ({usageStats.unused})
          </button>
        </div>
        <div className="w-full h-px bg-[hsl(var(--border))] my-1 md:hidden" />
        <button onClick={() => setActiveFolder("")} className={`px-3 h-8 rounded-full text-xs font-semibold border ${!activeFolder ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>Dossiers · {data.count}</button>
        {(data.folders || []).map((f) => (
          <button key={f} onClick={() => setActiveFolder(f)} className={`px-3 h-8 rounded-full text-xs font-semibold border flex items-center gap-1.5 ${activeFolder === f ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"}`}>
            <FolderOpen className="w-3 h-3" />{f}
          </button>
        ))}
      </Card>

      {/* Grid */}
      {loading ? (
        <Card className="p-20 border flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-20 border text-center text-sm text-[hsl(var(--muted-foreground))]">
          {q || usageFilter !== "all" ? "Aucun résultat pour ces filtres." : "Aucun média. Glissez-déposez vos premières photos dans la zone ci-dessus."}
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((m) => {
            const refs = usage[m.url] || [];
            return (
              <div key={m.url} className="group relative rounded-xl border border-[hsl(var(--border))] overflow-hidden bg-white hover:border-[color:var(--brand-sky)] hover:shadow-md transition flex flex-col" data-testid={`media-item-${m.name}`}>
                <div className="aspect-square bg-[hsl(var(--muted))]/30 cursor-zoom-in relative" onClick={() => setPreview(m)}>
                  <img src={m.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider shadow ${refs.length > 0 ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>
                    {refs.length > 0 ? `✓ ${refs.length}` : "Orphelin"}
                  </span>
                </div>
                <div className="px-2.5 py-2">
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] font-mono truncate">{m.folder || "/"}</div>
                  <div className="text-[11px] font-semibold truncate text-[color:var(--brand-navy)]">{m.name}</div>
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{Math.round(m.size_bytes / 1024)} Ko</div>
                </div>

                {/* Usage footer */}
                <div className="border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-2.5 py-2">
                  {refs.length > 0 ? (
                    <>
                      <div className="text-[9px] uppercase tracking-wider font-black text-emerald-700 mb-1 flex items-center gap-1">
                        <Info className="w-2.5 h-2.5" /> Utilisée dans
                      </div>
                      <ul className="space-y-0.5 max-h-24 overflow-y-auto pr-1">
                        {refs.slice(0, 5).map((r, i) => (
                          <li key={i}>
                            <a
                              href={r.public_path || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] hover:underline truncate w-full"
                              title={`${r.label} — ouvrir la page ${r.public_path}`}
                              data-testid={`media-usage-link-${i}`}
                            >
                              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate">{r.label}</span>
                            </a>
                          </li>
                        ))}
                        {refs.length > 5 && <li className="text-[9px] text-[hsl(var(--muted-foreground))] italic">+ {refs.length - 5} autre(s)…</li>}
                      </ul>
                    </>
                  ) : (
                    <div className="text-[10px] text-amber-700 font-semibold flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Non utilisée sur le site
                    </div>
                  )}
                </div>

                <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={(e) => { e.stopPropagation(); copyUrl(m.url); }} className="w-7 h-7 rounded-md bg-white/95 hover:bg-white shadow text-[color:var(--brand-navy)] flex items-center justify-center" title="Copier l'URL" data-testid={`media-copy-${m.name}`}>
                    {copied === m.url ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); del(m.url); }} className="w-7 h-7 rounded-md bg-white/95 hover:bg-red-50 shadow text-red-600 flex items-center justify-center" title={refs.length > 0 ? `Attention : utilisée dans ${refs.length} page(s)` : "Supprimer"} data-testid={`media-delete-${m.name}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="relative max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={preview.url} alt={preview.name} className="max-w-full max-h-[80vh] rounded-xl shadow-2xl" />
            <div className="mt-3 bg-white rounded-xl p-4 flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-[color:var(--brand-navy)]">{preview.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{preview.url}</div>
              </div>
              <Button onClick={() => copyUrl(preview.url)} variant="outline" size="sm"><Copy className="w-3.5 h-3.5 mr-1" />Copier URL</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
